import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'
export default class extends Controller {
  static targets = [
    'modal',
    'dialog',
    'selectionStep',
    'calendlyStep',
    'calendlyContainer',
    'radio',
    'selectedMeetingLabel',
    'closeButton',
    'continueButton',
    'backButton',
  ]

  static values = {
    meetingOptions: Array,
    createUrl: String,
    clientAdminProfileId: Number,
    adminId: Number,
    name: String,
    email: String,
  }

  connect() {
    this.selectedKey =
      this.radioTargets.find((radio) => radio.checked)?.value || null
    this.syncSelectedLabel()

    this.boundCalendlyMessageHandler = this.handleCalendlyMessage.bind(this)
    window.addEventListener('message', this.boundCalendlyMessageHandler)

    this.previouslyFocusedElement = null
  }

  disconnect() {
    window.removeEventListener('message', this.boundCalendlyMessageHandler)
  }

  open(event) {
    this.previouslyFocusedElement =
      event?.currentTarget || document.activeElement

    this.modalTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    this.showSelectionStep()
    this.focusInitialElement()
  }

  close() {
    this.modalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    this.resetCalendlyContainer()
    this.showSelectionStep()

    if (this.previouslyFocusedElement?.focus) {
      this.previouslyFocusedElement.focus()
    }
  }

  continueToCalendly() {
    const selected = this.selectedOption()
    if (!selected) return

    this.selectionStepTarget.classList.add('hidden')
    this.calendlyStepTarget.classList.remove('hidden')
    this.selectedMeetingLabelTarget.textContent = this.meetingLabel(selected)
    this.loadCalendly(selected.calendly_url)

    requestAnimationFrame(() => {
      if (this.hasBackButtonTarget) {
        this.backButtonTarget.focus()
      } else {
        this.focusFirstFocusable()
      }
    })
  }

  backToSelection() {
    this.resetCalendlyContainer()
    this.showSelectionStep()

    requestAnimationFrame(() => {
      if (this.hasCloseButtonTarget) {
        this.closeButtonTarget.focus()
      } else {
        this.focusFirstFocusable()
      }
    })
  }

  selectOption(event) {
    this.selectedKey = event.target.value
    this.syncSelectedLabel()
  }

  backdropClose(event) {
    if (event.target === this.modalTarget) this.close()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key === 'Tab') {
      this.maintainFocus(event)
    }
  }

  showSelectionStep() {
    this.calendlyStepTarget.classList.add('hidden')
    this.selectionStepTarget.classList.remove('hidden')
  }

  selectedOption() {
    if (!this.selectedKey) return null

    return (
      this.meetingOptionsValue.find(
        (option) => option.key === this.selectedKey,
      ) || null
    )
  }

  syncSelectedLabel() {
    const selected = this.selectedOption()
    if (!selected || !this.hasSelectedMeetingLabelTarget) return

    this.selectedMeetingLabelTarget.textContent = this.meetingLabel(selected)
  }

  meetingLabel(selected) {
    return `${selected.title} · ${selected.minutes} min`
  }

  loadCalendly(url) {
    this.resetCalendlyContainer()
    if (!url) return

    if (!window.Calendly) {
      console.error('Calendly script is not loaded')
      return
    }

    window.Calendly.initInlineWidget({
      url,
      parentElement: this.calendlyContainerTarget,
      prefill: {
        name: this.nameValue,
        email: this.emailValue,
      },
      utm: {},
    })
  }

  resetCalendlyContainer() {
    if (!this.hasCalendlyContainerTarget) return
    this.calendlyContainerTarget.innerHTML = ''
  }

  async handleCalendlyMessage(event) {
    if (!this.isCalendlyEvent(event)) return
    if (event.data.event !== 'calendly.event_scheduled') return
    if (!this.isOwnModalOpen()) return

    const created = await this.createCalendlyEvent(event.data.payload)
    if (!created) return

    setTimeout(() => {
      this.close()
    }, 500)
  }

  isOwnModalOpen() {
    return !!(
      this.hasModalTarget && !this.modalTarget.classList.contains('hidden')
    )
  }

  isCalendlyEvent(event) {
    return (
      event.origin === 'https://calendly.com' &&
      event.data?.event?.startsWith('calendly.')
    )
  }

  async createCalendlyEvent(payload) {
    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          calendly_event: {
            calendly_id: payload?.event?.uri?.split('/').pop(),
            calendly_user_id: payload?.invitee?.uri?.split('/').pop(),
            meeting_name: payload?.event_type?.name,
            start_time: payload?.event?.start_time,
            end_time: payload?.event?.end_time,
            client_admin_profile_id: this.clientAdminProfileIdValue,
            admin_id: this.adminIdValue,
          },
        }),
      })
      if (!response.ok) {
        throw new Error(`Calendly event create failed: ${response.status}`)
      }

      const html = await response.text()
      Turbo.renderStreamMessage(html)

      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  focusInitialElement() {
    requestAnimationFrame(() => {
      if (this.hasCloseButtonTarget) {
        this.closeButtonTarget.focus()
        return
      }

      this.focusFirstFocusable()
    })
  }

  focusFirstFocusable() {
    const first = this.focusableElements()[0]
    if (first) first.focus()
  }

  maintainFocus(event) {
    const focusable = this.focusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
      return
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusableElements() {
    const container = this.dialogTarget || this.modalTarget
    if (!container) return []

    const elements = Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled]):not([type="hidden"])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(','),
      ),
    )

    return elements.filter((element) => {
      if (!this.isVisible(element)) return false
      if (this.isInHiddenStep(element)) return false
      return true
    })
  }

  activeFocusContainer() {
    if (!this.modalTarget || this.modalTarget.classList.contains('hidden'))
      return null

    if (
      this.hasCalendlyStepTarget &&
      !this.calendlyStepTarget.classList.contains('hidden')
    ) {
      return this.calendlyStepTarget
    }

    if (
      this.hasSelectionStepTarget &&
      !this.selectionStepTarget.classList.contains('hidden')
    ) {
      return this.selectionStepTarget
    }

    return this.dialogTarget || this.modalTarget
  }

  isVisible(element) {
    return !!(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    )
  }

  isInHiddenStep(element) {
    const hiddenStep = element.closest('.hidden')
    if (!hiddenStep) return false

    if (
      hiddenStep === this.selectionStepTarget ||
      hiddenStep === this.calendlyStepTarget
    ) {
      return true
    }

    return false
  }

  get csrfToken() {
    return document
      .querySelector("meta[name='csrf-token']")
      ?.getAttribute('content')
  }
}
